import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Typography, Spin, Result, App, Modal, Tooltip, Tag, Input } from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  ExpandOutlined,
  ShrinkOutlined,
  SettingOutlined,
  TeamOutlined,
  GlobalOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import dashboardService from '../../api/dashboardService';
import { DashboardProvider } from './DashboardContext';
import { useDashboard } from './useDashboard';
import { PlayerProvider } from './PlayerContext';
import { StationObservationsProvider } from './StationObservationsProvider';
import { usePlayer } from './usePlayerContext';
import { DashboardPlayer } from './DashboardPlayer';
import { DashboardCanvas } from './DashboardCanvas';
import { AddWidgetDrawer } from './AddWidgetDrawer';
import { DashboardSettingsDrawer } from './DashboardSettingsDrawer';
import { Timeline } from '../../components/Timeline/Timeline';
import { useTimelineAvailability } from './useTimelineAvailability';
import { ROUTES } from '../../utils/constants';
import { useTheme } from '../../contexts/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { useEventSelection } from '../../contexts/useEventSelection';
import { isEventActive as checkEventActive } from '../../utils/eventSource';
import { useCollectionFreshness } from '../../hooks/useCollectionFreshness';

const { Title, Text } = Typography;

const DashboardViewContent = () => {
  const {
    dashboard,
    isEditing,
    isDirty,
    isSaving,
    isLoading,
    error,
    draftWidgets,
    draftSettings,
    draftTitle,
    draftDescription,
    loadDashboard,
    enterEditMode,
    cancelEdit,
    saveDashboard,
    updateSettings,
  } = useDashboard();

  const player = usePlayer();
  const { selectedEvent } = useEventSelection();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { authState } = useAuth();
  const { message } = App.useApp();
  const isOwner = dashboard?.owner_user_id === authState.user?.id;
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPopout, setIsPopout] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneTitle, setCloneTitle] = useState('');
  const [cloning, setCloning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const timelineCompact = draftSettings.timelineCompact ?? false;
  const handleTimelineCompactChange = useCallback((v: boolean) => {
    updateSettings({ timelineCompact: v });
  }, [updateSettings]);

  const playerCompact = draftSettings.playerCompact ?? false;
  const handlePlayerCompactChange = useCallback((v: boolean) => {
    updateSettings({ playerCompact: v });
  }, [updateSettings]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  useEffect(() => {
    if (!isPopout) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsPopout(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isPopout]);

  const isEventActive = checkEventActive(selectedEvent);
  const metadataTimestamps = useCollectionFreshness(['forecast_daily', 'forecast_hourly'], {
    interval: 30_000,
    enabled: isEventActive,
  });
  const [timelineRefreshCounter, setTimelineRefreshCounter] = useState(0);
  const lastTimelineFetchRef = useRef<string>('');
  useEffect(() => {
    const latest = metadataTimestamps.forecast_daily || metadataTimestamps.forecast_hourly;
    if (latest && latest > lastTimelineFetchRef.current) {
      lastTimelineFetchRef.current = latest;
      setTimelineRefreshCounter(c => c + 1);
    }
  }, [metadataTimestamps]);

  const {
    radarActivityAvailability,
    stationActivityAvailability,
    forecastAvailability,
    loading: timelineLoading,
  } = useTimelineAvailability(selectedEvent, timelineRefreshCounter);

  const {
    setSelectedDateRange,
    liveStartMinutes,
    liveEndMinutesForRadar,
    setLiveStartMinutes,
    setLiveEndMinutesForRadar,
    setIsLiveMode,
    setPlaybackSpeed,
    setAutoLoop,
    setPlaybackDock,
  } = player;

  const dashboardId = dashboard?.dashboard_id;
  const prevEditingRef = useRef(false);

  const hasRadarMapWidget = useMemo(() => {
    return draftWidgets.some((w) => {
      if (w.type !== 'radar-map') return false;
      const provider = w.config?.mapTileProvider as string | undefined;
      return (
        !provider ||
        provider === 'radar' ||
        provider === 'radar-l2' ||
        provider === 'radar-l2-numeric'
      );
    });
  }, [draftWidgets]);

  const handleDateRangeChange = useCallback(
    (startDate: Date, endDate: Date) => {
      const start = new Date(startDate);
      start.setSeconds(0, 0);
      const end = new Date(endDate);
      end.setSeconds(59, 999);
      setSelectedDateRange({ start, end });
    },
    [setSelectedDateRange],
  );

  useEffect(() => {
    if (id) loadDashboard(id);
  }, [id, loadDashboard]);


  // Seed player state from dashboard settings on load
  useEffect(() => {
    if (!dashboard) return;
    const s = dashboard.settings;
    if (s.playbackSpeed != null) setPlaybackSpeed(s.playbackSpeed);
    if (s.autoLoop != null) setAutoLoop(s.autoLoop);
    if (s.playbackDock != null) setPlaybackDock(s.playbackDock);
    if (s.liveStartMinutes != null) setLiveStartMinutes(s.liveStartMinutes);
    if (s.liveEndMinutesForRadar != null) setLiveEndMinutesForRadar(s.liveEndMinutesForRadar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId]);

  // Reseed player from saved settings when exiting edit mode (cancel or save)
  useEffect(() => {
    if (prevEditingRef.current && !isEditing) {
      setPlaybackSpeed(draftSettings.playbackSpeed ?? 1);
      setAutoLoop(draftSettings.autoLoop ?? false);
      setPlaybackDock(draftSettings.playbackDock ?? 'now');
      setLiveStartMinutes(draftSettings.liveStartMinutes ?? -5);
      setLiveEndMinutesForRadar(draftSettings.liveEndMinutesForRadar ?? 30);
    }
    prevEditingRef.current = isEditing;
  }, [isEditing, draftSettings, setPlaybackSpeed, setAutoLoop, setPlaybackDock, setLiveStartMinutes, setLiveEndMinutesForRadar]);

  const handleSave = async () => {
    const success = await saveDashboard();
    if (success) {
      message.success('Dashboard saved');
    } else {
      message.error('Failed to save dashboard');
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      Modal.confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to discard them?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Keep Editing',
        onOk: cancelEdit,
      });
    } else {
      cancelEdit();
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <Result
        status="error"
        title="Dashboard not found"
        subTitle={error || 'The dashboard could not be loaded.'}
        extra={
          <Button type="primary" onClick={() => navigate(ROUTES.DASHBOARDS)}>
            Back to Dashboards
          </Button>
        }
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: theme === 'dark' ? '#141414' : '#fff',
      }}
    >
      {/* Toolbar — hidden in fullscreen mode */}
      {!isFullscreen && (
        <div
          style={{
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)',
            flexShrink: 0,
          }}
        >
          <Space align="center" size={12}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                if (isEditing && isDirty) {
                  Modal.confirm({
                    title: 'Leave without saving?',
                    content: 'You have unsaved changes.',
                    okText: 'Leave',
                    okType: 'danger',
                    cancelText: 'Stay',
                    onOk: () => navigate(ROUTES.DASHBOARDS),
                  });
                } else {
                  navigate(ROUTES.DASHBOARDS);
                }
              }}
            />
            <div>
              <Space align="center" size={8}>
                <Title level={5} style={{ margin: 0 }}>
                  {isEditing ? draftTitle : dashboard.title}
                </Title>
                {dashboard.visibility !== 'private' && (
                  <Tag
                    icon={dashboard.visibility === 'team' ? <TeamOutlined /> : <GlobalOutlined />}
                    color={dashboard.visibility === 'team' ? 'blue' : 'green'}
                    style={{ margin: 0 }}
                  >
                    {dashboard.visibility}
                  </Tag>
                )}
                {!isOwner && (
                  <Tag color="orange" style={{ margin: 0 }}>shared</Tag>
                )}
              </Space>
              {(isEditing ? draftDescription : dashboard.description) && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {isEditing ? draftDescription : dashboard.description}
                </Text>
              )}
            </div>
          </Space>

          <Space>
            {isEditing ? (
              <>
                <Tooltip title="Settings">
                  <Button
                    icon={<SettingOutlined />}
                    onClick={() => setSettingsDrawerOpen(true)}
                  />
                </Tooltip>
                <Tooltip title="Add Widget">
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setAddDrawerOpen(true)}
                  />
                </Tooltip>
                <Tooltip title="Cancel">
                  <Button icon={<CloseOutlined />} onClick={handleCancel} />
                </Tooltip>
                <Tooltip title="Save">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={!isDirty}
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Pop out">
                  <Button
                    icon={<ExpandOutlined />}
                    onClick={() => setIsPopout(true)}
                  />
                </Tooltip>
                <Tooltip title="Fullscreen">
                  <Button
                    icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    onClick={toggleFullscreen}
                  />
                </Tooltip>
                {isOwner ? (
                  <Tooltip title="Edit">
                    <Button icon={<EditOutlined />} onClick={enterEditMode} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Clone to my dashboards">
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => {
                        setCloneTitle(`${dashboard.title} (Copy)`);
                        setCloneModalOpen(true);
                      }}
                    >
                      Clone
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
          </Space>
        </div>
      )}

      {/* Floating exit-fullscreen button */}
      {isFullscreen && (
        <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
          <Button
            size="small"
            icon={<FullscreenExitOutlined />}
            onClick={toggleFullscreen}
          >
            Exit Fullscreen (Esc)
          </Button>
        </div>
      )}

      {/* Timeline — date range selector */}
      <div style={{ flexShrink: 0 }}>
        <Timeline
          onDateRangeChange={handleDateRangeChange}
          radarActivityAvailability={radarActivityAvailability}
          stationActivityAvailability={stationActivityAvailability}
          forecastAvailability={forecastAvailability}
          mapTileProvider="radar-l2"
          liveStartMinutes={liveStartMinutes}
          liveEndMinutesForRadar={hasRadarMapWidget ? liveEndMinutesForRadar : 0}
          onLiveStartMinutesChange={setLiveStartMinutes}
          onLiveEndMinutesForRadarChange={hasRadarMapWidget ? setLiveEndMinutesForRadar : undefined}
          onLiveModeChange={setIsLiveMode}
          compact={timelineCompact}
          onCompactChange={isEditing ? handleTimelineCompactChange : undefined}
        />
      </div>

      {/* Player bar — auto-hides when no timeline data */}
      <DashboardPlayer
        loading={timelineLoading}
        compact={playerCompact}
        onCompactChange={handlePlayerCompactChange}
      />

      {/* Grid area */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: 0,
          background: theme === 'dark' ? '#141414' : '#f0f2f5',
        }}
      >
        {draftWidgets.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50vh',
              gap: 16,
            }}
          >
            <Text type="secondary" style={{ fontSize: 16 }}>
              This dashboard is empty
            </Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                if (!isEditing) enterEditMode();
                setAddDrawerOpen(true);
              }}
            >
              Add Your First Widget
            </Button>
          </div>
        ) : (
          <DashboardCanvas />
        )}
      </div>

      <AddWidgetDrawer open={addDrawerOpen} onClose={() => setAddDrawerOpen(false)} />
      <DashboardSettingsDrawer open={settingsDrawerOpen} onClose={() => setSettingsDrawerOpen(false)} />

      <Modal
        title="Clone Dashboard"
        open={cloneModalOpen}
        onCancel={() => setCloneModalOpen(false)}
        onOk={async () => {
          if (!cloneTitle.trim()) return;
          setCloning(true);
          try {
            const res = await dashboardService.cloneDashboard(dashboard.dashboard_id, cloneTitle.trim());
            if (res.success && res.data) {
              message.success('Dashboard cloned');
              setCloneModalOpen(false);
              navigate(`${ROUTES.DASHBOARDS}/${res.data.dashboard_id}`);
            } else {
              message.error('Failed to clone dashboard');
            }
          } catch {
            message.error('Failed to clone dashboard');
          } finally {
            setCloning(false);
          }
        }}
        confirmLoading={cloning}
        okButtonProps={{ disabled: !cloneTitle.trim() }}
        okText="Clone"
        destroyOnHidden
      >
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Name for the cloned dashboard
          </Text>
          <Input
            value={cloneTitle}
            onChange={(e) => setCloneTitle(e.target.value)}
            placeholder="Dashboard title"
            maxLength={100}
            autoFocus
          />
        </div>
      </Modal>

      {/* Pop-out overlay — true full-viewport, no Ant Design modal wrapper */}
      {isPopout && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme === 'dark' ? '#141414' : '#fff',
          }}
        >
          <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1001 }}>
            <Button
              size="small"
              icon={<ShrinkOutlined />}
              onClick={() => setIsPopout(false)}
            >
              Exit Pop Out (Esc)
            </Button>
          </div>
          <div style={{ flexShrink: 0 }}>
            <Timeline
              onDateRangeChange={handleDateRangeChange}
              radarActivityAvailability={radarActivityAvailability}
              stationActivityAvailability={stationActivityAvailability}
              forecastAvailability={forecastAvailability}
              mapTileProvider="radar-l2"
              liveStartMinutes={liveStartMinutes}
              liveEndMinutesForRadar={hasRadarMapWidget ? liveEndMinutesForRadar : 0}
              onLiveStartMinutesChange={setLiveStartMinutes}
              onLiveEndMinutesForRadarChange={hasRadarMapWidget ? setLiveEndMinutesForRadar : undefined}
              onLiveModeChange={setIsLiveMode}
              compact={timelineCompact}
              onCompactChange={isEditing ? handleTimelineCompactChange : undefined}
            />
          </div>
          <DashboardPlayer
            loading={timelineLoading}
            compact={playerCompact}
            onCompactChange={handlePlayerCompactChange}
          />
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              background: theme === 'dark' ? '#141414' : '#f0f2f5',
            }}
          >
            <DashboardCanvas />
          </div>
        </div>
      )}
    </div>
  );
};

export const DashboardViewPage = () => {
  return (
    <DashboardProvider>
      <PlayerProvider>
        <StationObservationsProvider>
          <DashboardViewContent />
        </StationObservationsProvider>
      </PlayerProvider>
    </DashboardProvider>
  );
};
